import { addDays, formatLongDate } from './dates';
import { firstName } from './palette';
import { type Lookup, isScheduledIn } from './schedule';
import type { ScheduleEvent } from './types';

/**
 * Agenda pessoal: as escalas do membro no Google Agenda, na agenda do celular (Apple) ou
 * no Outlook.
 *
 * - **Assinatura** (`buildIcs`): um calendário iCalendar (RFC 5545) que o aplicativo de
 *   agenda busca de tempos em tempos pelo link secreto do membro — assim a agenda se
 *   atualiza quando a escala muda (entra, sai, muda de horário).
 * - **Um evento só** (`googleCalendarUrl`): o link "Adicionar ao Google Agenda" de cada
 *   escala em "Minha agenda".
 *
 * Só entram escalas **publicadas** — rascunho não vai para a agenda de ninguém.
 */

/** Duração presumida de um culto ou evento (a escala não guarda o fim). */
export const EVENT_DURATION_MINUTES = 120;

/** Fuso padrão da instituição (o mesmo de `settings.timezone` no server). */
export const DEFAULT_TIMEZONE = 'America/Sao_Paulo';

/** Quantos dias de escalas passadas a assinatura mantém (o histórico recente continua na agenda). */
export const CALENDAR_PAST_DAYS = 60;

export interface CalendarItem {
  /** Estável por evento e membro: a agenda reconhece a mesma escala entre atualizações. */
  uid: string;
  eventId: string;
  title: string;
  date: string;
  time: string;
  description: string;
}

/** As escalas publicadas do membro, como entradas de agenda ("Portaria · Culto da Manhã"). */
export function memberCalendarItems(
  events: readonly ScheduleEvent[],
  memberId: string,
  lookup: Lookup,
  today: string
): CalendarItem[] {
  const from = addDays(today, -CALENDAR_PAST_DAYS);

  return events
    .filter((e) => e.published && e.date >= from && isScheduledIn(e, memberId))
    .sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time))
    .map((event) => {
      const roleId = Object.entries(event.slots).find(([, ids]) => ids.includes(memberId))?.[0] ?? '';
      const found = lookup.roles.get(roleId);
      const roleName = found?.role.name ?? 'Escala';
      const mates = (event.slots[roleId] ?? [])
        .filter((id) => id !== memberId)
        .map((id) => lookup.members.get(id))
        .flatMap((m) => (m ? [firstName(m.name)] : []));
      const lines = [
        found ? `${found.ministry.name} · ${roleName}` : roleName,
        mates.length ? `Com ${mates.join(', ')}` : '',
        'Escala publicada no Live Gospel.',
      ].filter(Boolean);

      return {
        uid: `${event.id}-${memberId}@escala`,
        eventId: event.id,
        title: `${roleName} · ${event.title}`,
        date: event.date,
        time: event.time,
        description: lines.join('\n'),
      };
    });
}

/** `2026-10-11` + `19:00` (+ minutos) → `20261011T190000`, em horário local. */
export function icsDateTime(date: string, time: string, plusMinutes = 0): string {
  const [h = 0, m = 0] = time.split(':').map(Number);
  const total = h * 60 + m + plusMinutes;
  const day = addDays(date, Math.floor(total / 1440));
  const minutes = total % 1440;
  const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mm = String(minutes % 60).padStart(2, '0');

  return `${day.replaceAll('-', '')}T${hh}${mm}00`;
}

/** Instante UTC no formato do iCalendar (`20261006T120000Z`). */
function icsStamp(at: number): string {
  return new Date(at)
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}/, '');
}

/** Escapa texto de propriedade (RFC 5545 §3.3.11). */
export function icsEscape(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/([,;])/g, '\\$1');
}

function utf8Length(codePoint: number): number {
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;

  return codePoint < 0x10000 ? 3 : 4;
}

/** Quebra linhas maiores que 75 octetos (RFC 5545 §3.1), sem partir um caractere UTF-8. */
export function icsFold(line: string): string {
  const parts: string[] = [];
  let current = '';
  let size = 0;

  for (const char of line) {
    const bytes = utf8Length(char.codePointAt(0) ?? 0);
    const limit = parts.length === 0 ? 75 : 74;

    if (size + bytes > limit) {
      parts.push(current);
      current = '';
      size = 0;
    }

    current += char;
    size += bytes;
  }

  parts.push(current);

  return parts.join('\r\n ');
}

export interface IcsOptions {
  /** Nome do calendário no aplicativo ("Live Gospel · Ana Souza"). */
  name: string;
  timezone?: string;
  /** Momento da geração (`DTSTAMP`). */
  now?: number;
}

/** O calendário inteiro, com fim de linha CRLF. */
export function buildIcs(items: readonly CalendarItem[], options: IcsOptions): string {
  const tz = options.timezone ?? DEFAULT_TIMEZONE;
  const stamp = icsStamp(options.now ?? Date.now());
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Escala//Agenda pessoal//PT-BR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${icsEscape(options.name)}`,
    `X-WR-TIMEZONE:${tz}`,
    'REFRESH-INTERVAL;VALUE=DURATION:PT1H',
    'X-PUBLISHED-TTL:PT1H',
    ...items.flatMap((item) => [
      'BEGIN:VEVENT',
      `UID:${item.uid}`,
      `DTSTAMP:${stamp}`,
      `DTSTART;TZID=${tz}:${icsDateTime(item.date, item.time)}`,
      `DTEND;TZID=${tz}:${icsDateTime(item.date, item.time, EVENT_DURATION_MINUTES)}`,
      `SUMMARY:${icsEscape(item.title)}`,
      `DESCRIPTION:${icsEscape(item.description)}`,
      'END:VEVENT',
    ]),
    'END:VCALENDAR',
  ];

  return `${lines.map(icsFold).join('\r\n')}\r\n`;
}

/** "Adicionar ao Google Agenda" de uma escala (abre o evento já preenchido). */
export function googleCalendarUrl(item: CalendarItem, timezone: string = DEFAULT_TIMEZONE): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: item.title,
    dates: `${icsDateTime(item.date, item.time)}/${icsDateTime(item.date, item.time, EVENT_DURATION_MINUTES)}`,
    details: item.description,
    ctz: timezone,
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Rótulo acessível do link de cada escala. */
export function calendarAria(item: CalendarItem): string {
  return `Adicionar ${item.title}, ${formatLongDate(item.date)} às ${item.time}, ao Google Agenda`;
}

export interface SubscribeLinks {
  /** O endereço do calendário (https), para colar em qualquer aplicativo. */
  url: string;
  /** O mesmo com `webcal://`: o celular abre direto na agenda (Apple, Outlook). */
  webcal: string;
  /** Abre o Google Agenda já perguntando se quer adicionar a assinatura. */
  google: string;
  /** Outlook na web. */
  outlook: string;
}

export function subscribeLinks(feedUrl: string, name = 'Live Gospel'): SubscribeLinks {
  const webcal = feedUrl.replace(/^https?:\/\//, 'webcal://');

  return {
    url: feedUrl,
    webcal,
    google: `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`,
    outlook: `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(feedUrl)}&name=${encodeURIComponent(name)}`,
  };
}
